import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { api, ApiError } from '../api';
import { can, fieldVisible, type Me } from '../auth/me';
import { formatPln } from '../catalog/money';
import { useEscapeLayer } from '../ui/Dialog';
import { CONTRACTORS_KEY, type Contractor, type Kind } from './ContractorForm';

/**
 * Kontrahenci → Info (owner 2026-10-23 19:27: „dodaj menu info i tam informacje ile komus zaplacilem lacznie i ile ktos
 * zaplacil mi. do tego info pewnie pozniej cos jeszcze dojdzie”). The server sends the rows the user may see
 * (`GET /api/finance/contractors/:id/info`); a new row there shows here without a change.
 */
export type InfoRow = { id: string; label: string; amountGrosze: number };

const RECEIVABLES = 'payments.receivables';
const PURCHASES = 'purchases.purchases';

/**
 * Whether any row of the Info can be there for this user and kind (the server decides in the end): „Zapłacili mi”
 * (a client) needs the payments' amounts, „Zapłaciłem” (a supplier) those and the purchase invoices' amounts.
 * Without any, the „Info” button is not shown at all (no empty panel, no zeros).
 */
export function infoAvailable(me: Me, kind: Kind): boolean {
  const payments = can(me, RECEIVABLES, 'view') && fieldVisible(me, RECEIVABLES, 'amountGrosze');
  const received = payments && fieldVisible(me, RECEIVABLES, 'paidGrosze');
  const paid = payments && can(me, PURCHASES, 'view') && fieldVisible(me, PURCHASES, 'grossGrosze');
  return (kind !== 'supplier' && received) || (kind !== 'client' && paid);
}

/** „Od początku” and the last five years. */
function yearChoices(now = new Date()): number[] {
  const y = now.getFullYear();
  return [y, y - 1, y - 2, y - 3, y - 4];
}

/**
 * „ⓘ Info” on the row (shown on hover, in a place kept for it) and its small panel. Closes on Escape (the stack of
 * layers, #96), a click outside or the button again; no ×. A click outside is a `pointerdown`, not a blur: Safari does
 * not focus a clicked button (#110), so a blur would close the panel before the year was chosen.
 */
export function ContractorInfo({ contractor }: { contractor: Contractor }) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState('');
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (e.target instanceof Node && !root.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);
  useEscapeLayer(() => {
    setOpen(false);
    button.current?.focus();
  }, open);

  const info = useQuery({
    queryKey: [...CONTRACTORS_KEY, 'info', contractor.id, year],
    enabled: open,
    queryFn: () => api<{ rows: InfoRow[] }>(`/api/finance/contractors/${contractor.id}/info${year ? `?year=${year}` : ''}`),
  });

  return (
    <div className={open ? 'con-info open' : 'con-info'} ref={root} data-feature-id="finance.contractor_info">
      <button ref={button} type="button" className="btn small ghost con-hover" aria-label={`Info: ${contractor.name}`} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => setOpen((v) => !v)}>
        <span aria-hidden="true">ⓘ</span> Info
      </button>
      {open && (
        <div className="con-info-pop" role="dialog" id={id} aria-label={`Info: ${contractor.name}`}>
          <label className="con-info-year">
            <span>Okres</span>
            <select value={year} onChange={(e) => setYear(e.target.value)}>
              <option value="">Od początku</option>
              {yearChoices().map((y) => (
                <option key={y} value={String(y)}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          {info.isLoading && <p className="muted">Wczytywanie…</p>}
          {info.error && (
            <p className="alert" role="alert">
              {info.error instanceof ApiError ? info.error.message : 'Nie udało się wczytać'}
            </p>
          )}
          {info.data && info.data.rows.length === 0 && <p className="muted">Brak informacji, które możesz zobaczyć.</p>}
          {info.data && info.data.rows.length > 0 && (
            <dl className="con-info-rows">
              {info.data.rows.map((r) => (
                <div key={r.id} className="con-info-row">
                  <dt>{r.label}</dt>
                  <dd className="num">{formatPln(r.amountGrosze)}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}
    </div>
  );
}
